import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { useThemeClasses } from "@/hooks/useThemeClasses";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Receipt, Search, Eye, Calendar, DollarSign, ArrowUpDown, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";

const ITEMS_PER_PAGE = 15;

const Vendas = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const lojaSlug = slug || user?.loja_slug;
  const { isDark, card, input, textPrimary, textSecondary, textMuted, tableRow, button } = useThemeClasses();

  const [vendas, setVendas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [orderConfig, setOrderConfig] = useState({ key: "data", direction: "desc" });
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => { if (lojaSlug) fetchVendas(); }, [lojaSlug]);

  // Reset page when search changes
  useEffect(() => { setCurrentPage(1); }, [search]);

  const fetchVendas = async () => {
    try {
      const response = await axios.get(`${API}/loja/${lojaSlug}/vendas`);
      setVendas(response.data);
    } catch (error) {
      toast.error("Erro ao carregar vendas");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
  const formatDate = (d) => new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const formatPayment = (p) => ({ 'dinheiro': 'Dinheiro', 'pix': 'PIX', 'cartao_credito': 'Cartão de Crédito', 'cartao_debito': 'Cartão de Débito', 'transferencia': 'Transferência' }[p] || p);

  const handleSort = (key) => {
    setOrderConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  const renderSortIcon = (key) => {
    if (orderConfig.key !== key) return <ArrowUpDown className="w-4 h-4 opacity-30" />;
    return orderConfig.direction === "asc" ? <span className="text-xs">↑</span> : <span className="text-xs">↓</span>;
  };

  const filteredVendas = vendas
    .filter((v) =>
      v.cliente_nome?.toLowerCase().includes(search.toLowerCase()) ||
      v.id.includes(search) ||
      formatDate(v.data).includes(search)
    )
    .sort((a, b) => {
      let valueA, valueB;
      switch (orderConfig.key) {
        case "data":
          valueA = new Date(a.data).getTime();
          valueB = new Date(b.data).getTime();
          break;
        case "valor":
          valueA = a.valor_total || 0;
          valueB = b.valor_total || 0;
          break;
        case "cliente":
          valueA = a.cliente_nome?.toLowerCase() || "";
          valueB = b.cliente_nome?.toLowerCase() || "";
          break;
        default:
          return 0;
      }
      if (valueA < valueB) return orderConfig.direction === "asc" ? -1 : 1;
      if (valueA > valueB) return orderConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

  // Pagination
  const totalPages = Math.ceil(filteredVendas.length / ITEMS_PER_PAGE);
  const paginatedVendas = filteredVendas.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const totalVendas = filteredVendas.reduce((sum, v) => sum + (v.valor_total || 0), 0);
  const totalLucroEstimado = filteredVendas.reduce((sum, v) => sum + (v.lucro_estimado || 0), 0);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-[#D4AF37]">Carregando...</div></div>;

  return (
    <div className="space-y-6" data-testid="vendas-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center"><Receipt className="w-5 h-5 text-purple-500" /></div>
          <div>
            <h1 className={`text-2xl font-bold font-['Outfit'] ${textPrimary}`}>Vendas</h1>
            <p className={`text-sm ${textMuted}`}>{filteredVendas.length} vendas encontradas</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Card className={card}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center"><DollarSign className="w-5 h-5 text-[#D4AF37]" /></div>
              <div>
                <p className={`text-xs uppercase tracking-wider ${textMuted}`}>Total</p>
                <p className="text-lg font-bold text-[#D4AF37]">{formatCurrency(totalVendas)}</p>
              </div>
            </CardContent>
          </Card>
          <Card className={card}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-green-500" /></div>
              <div>
                <p className={`text-xs uppercase tracking-wider ${textMuted}`}>Lucro Est.</p>
                <p className="text-lg font-bold text-green-500">{formatCurrency(totalLucroEstimado)}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Card className={card}>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder="Buscar por cliente, ID ou data..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`pl-10 ${input}`}
              data-testid="search-venda"
            />
          </div>
        </CardContent>
      </Card>

      <Card className={card}>
        <CardContent className="p-0">
          {paginatedVendas.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className={`${isDark ? 'border-white/5' : 'border-gray-100'} hover:bg-transparent`}>
                      <TableHead onClick={() => handleSort("data")} className={`cursor-pointer uppercase text-xs whitespace-nowrap ${textMuted}`}>
                        <div className="flex items-center gap-2">Data {renderSortIcon("data")}</div>
                      </TableHead>
                      <TableHead onClick={() => handleSort("cliente")} className={`cursor-pointer uppercase text-xs whitespace-nowrap ${textMuted}`}>
                        <div className="flex items-center gap-2">Cliente {renderSortIcon("cliente")}</div>
                      </TableHead>
                      <TableHead className={`uppercase text-xs whitespace-nowrap ${textMuted}`}>Itens</TableHead>
                      <TableHead className={`uppercase text-xs whitespace-nowrap hidden sm:table-cell ${textMuted}`}>Pagamento</TableHead>
                      <TableHead onClick={() => handleSort("valor")} className={`cursor-pointer uppercase text-xs whitespace-nowrap ${textMuted}`}>
                        <div className="flex items-center gap-2">Valor {renderSortIcon("valor")}</div>
                      </TableHead>
                      <TableHead className={`uppercase text-xs whitespace-nowrap hidden lg:table-cell ${textMuted}`}>Lucro Est.</TableHead>
                      <TableHead className={`uppercase text-xs text-right whitespace-nowrap ${textMuted}`}>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedVendas.map((venda) => (
                      <TableRow key={venda.id} className={tableRow}>
                        <TableCell className={`whitespace-nowrap ${textMuted}`}>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-500 hidden sm:block" />
                            {formatDate(venda.data)}
                          </div>
                        </TableCell>
                        <TableCell className={`font-medium whitespace-nowrap ${textSecondary}`}>{venda.cliente_nome}</TableCell>
                        <TableCell className="whitespace-nowrap">
                          <div className="flex flex-wrap gap-1">
                            {(venda.itens_parsed?.length || 0) > 0 && (
                              <span className="px-2 py-1 bg-blue-500/10 text-blue-500 text-xs font-semibold rounded">
                                {venda.itens_parsed.length} {venda.itens_parsed.length === 1 ? 'celular' : 'celulares'}
                              </span>
                            )}
                            {(venda.itens_acessorios_parsed?.length || 0) > 0 && (
                              <span className="px-2 py-1 bg-purple-500/10 text-purple-400 text-xs font-semibold rounded">
                                {venda.itens_acessorios_parsed.reduce((sum, a) => sum + a.quantidade, 0)} {venda.itens_acessorios_parsed.reduce((sum, a) => sum + a.quantidade, 0) === 1 ? 'acessório' : 'acessórios'}
                              </span>
                            )}
                            {!(venda.itens_parsed?.length || 0) && !(venda.itens_acessorios_parsed?.length || 0) && (
                              <span className="px-2 py-1 bg-gray-500/10 text-gray-500 text-xs font-semibold rounded">
                                0 itens
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className={`whitespace-nowrap hidden sm:table-cell ${textMuted}`}>{formatPayment(venda.forma_pagamento)}</TableCell>
                        <TableCell className="text-[#D4AF37] font-semibold whitespace-nowrap">{formatCurrency(venda.valor_total)}</TableCell>
                        <TableCell className="text-green-500 font-semibold whitespace-nowrap hidden lg:table-cell">{formatCurrency(venda.lucro_estimado)}</TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Link to={`/${lojaSlug}/vendas/${venda.id}`}>
                            <Button size="sm" variant="ghost" className={button.ghost} data-testid={`btn-view-${venda.id}`}>
                              <Eye className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Ver</span>
                            </Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredVendas.length}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setCurrentPage}
              />
            </>
          ) : (
            <div className="p-12 text-center">
              <Receipt className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className={textMuted}>Nenhuma venda encontrada</p>
              <Link to={`/${lojaSlug}/ponto-venda`}>
                <Button className="mt-4 bg-[#D4AF37] text-black font-bold hover:bg-[#B5952F]">Realizar Venda</Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Vendas;
